import { $fetchAllo } from '@/services/api-allo/client';
import { type ContactsSearchParams, ContactsView, parseUrlFilters } from '@/views/contacts';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/contacts')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): ContactsSearchParams => {
    return {
      filters: typeof search.filters === 'string' ? search.filters : undefined,
      sortField: typeof search.sortField === 'string' ? search.sortField : undefined,
      sortOrder:
        search.sortOrder === 'asc' || search.sortOrder === 'desc' ? search.sortOrder : undefined,
      viewId: typeof search.viewId === 'string' ? search.viewId : undefined,
      preselectDemo:
        search.preselectDemo === true || search.preselectDemo === 'true' ? true : undefined,
    };
  },
  loaderDeps: ({ search }) => ({
    hasFilter: parseUrlFilters(search.filters).length > 0,
  }),
  loader: async ({ context, deps }) => {
    const { queryClient } = context as { queryClient: QueryClient };

    if (deps.hasFilter) {
      return;
    }

    const init = {
      params: {
        query: {
          'page[size]': 50 as const,
          'sort_by[last_activity]': 'desc' as const,
          extend: 'custom_properties' as const,
        },
      },
    };

    await queryClient
      .prefetchInfiniteQuery({
        queryKey: ['get', '/v1/contact', init],
        queryFn: async ({ pageParam, signal }) => {
          const { data } = await $fetchAllo.GET('/v1/contact', {
            ...init,
            params: {
              ...init.params,
              query: {
                ...init.params.query,
                'page[number]': pageParam,
              },
            },
            signal,
          });
          return data;
        },
        initialPageParam: 0,
        staleTime: 30 * 60_000,
      })
      .catch((error) => {
        console.warn('Failed to prefetch contacts:', error);
      });
  },
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const searchParams = Route.useSearch();

  const handleNavigate = (updater: (prev: ContactsSearchParams) => ContactsSearchParams) => {
    navigate({ search: updater });
  };

  return <ContactsView searchParams={searchParams} onNavigate={handleNavigate} />;
}
